import { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import CardHeader from "@material-ui/core/CardHeader";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import { Pagination, Skeleton } from "@material-ui/lab";

import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    padding: 0,
    borderBottom: `0px solid ${theme.palette.divider}`,
  },
  list: {
    backgroundColor: theme.palette.background.paper,
    overflowY: "auto",
  },
  listItem: {
    display: "flex",
    padding: 0,
  },
}));

const LoadingItem = () => (
  <ListItem style={{ padding: ".5rem" }}>
    <Skeleton />
  </ListItem>
);

function getFilteredPerfis(perfis, filter) {
  if (!filter) return perfis;
  const value = filter.toLowerCase();
  return perfis.filter(
    (perfil) =>
      perfil.NomePerfil?.toLowerCase().includes(value) ||
      perfil.Descricao?.toLowerCase().includes(value)
  );
}

export default function PerfisList({
  selectedPerfilIds = [],
  onSelectedChange,
  label = "Perfis",
  rowsPerPage = 5,
}) {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    async function fetchPerfis() {
      setLoading(true);
      try {
        const { Success, Result } = await Api.get(
          "list-profile",
          "adminportal",
          "",
          true
        );
        if (Success) {
          setPerfis(Result || []);
        }
      } catch (error) {
        snackbar.showMessage(error, {
          severity: "error",
        });      } finally {
        setLoading(false);
      }
    }
    fetchPerfis();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [filter]);

  const filteredPerfis = getFilteredPerfis(perfis, filter);
  const totalPages = Math.ceil(filteredPerfis.length / rowsPerPage);
  const paginatedPerfis = filteredPerfis.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const allSelected =
    selectedPerfilIds.length === filteredPerfis.length &&
    filteredPerfis.length > 0;
  const allPageSelected =
    paginatedPerfis.length > 0 &&
    paginatedPerfis.every((perfil) =>
      selectedPerfilIds.includes(perfil.IdPerfil)
    );
  const somePageSelected = paginatedPerfis.some((perfil) =>
    selectedPerfilIds.includes(perfil.IdPerfil)
  );

  const handleToggleAll = () => {
    if (!somePageSelected) {
      const newSelected = [
        ...selectedPerfilIds,
        ...paginatedPerfis
          .map((p) => p.IdPerfil)
          .filter((id) => !selectedPerfilIds.includes(id)),
      ];
      onSelectedChange(newSelected);
    } else if (allPageSelected && !allSelected) {
      onSelectedChange(filteredPerfis.map((p) => p.IdPerfil));
    } else if (allSelected) {
      onSelectedChange([]);
    } else {
      const newSelected = [
        ...selectedPerfilIds,
        ...paginatedPerfis
          .map((p) => p.IdPerfil)
          .filter((id) => !selectedPerfilIds.includes(id)),
      ];
      onSelectedChange(newSelected);
    }
  };

  const handleTogglePerfil = (perfilId) => {
    if (selectedPerfilIds.includes(perfilId)) {
      onSelectedChange(selectedPerfilIds.filter((id) => id !== perfilId));
    } else {
      onSelectedChange([...selectedPerfilIds, perfilId]);
    }
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  const handlePageClick = (event, value) => {
    setCurrentPage(value - 1);
  };

  let selectAllTooltip = "";
  if (!somePageSelected) {
    selectAllTooltip = "Selecionar todos os perfis desta página";
  } else if (allPageSelected && !allSelected) {
    selectAllTooltip = "Selecionar todos os perfis";
  } else if (allSelected) {
    selectAllTooltip = "Desmarcar todos os perfis";
  } else {
    selectAllTooltip = "Selecionar todos os perfis desta página";
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <CardHeader
          className={classes.cardHeader}
          avatar={
            <Tooltip title={selectAllTooltip} arrow>
              <Checkbox
                onClick={handleToggleAll}
                checked={allSelected}
                indeterminate={!allSelected && somePageSelected}
                disabled={paginatedPerfis.length === 0}
                color="primary"
              />
            </Tooltip>
          }
          title={label}
          titleTypographyProps={{ color: "primary" }}
          subheader={`${selectedPerfilIds.length}/${filteredPerfis.length} selecionados`}
          subheaderTypographyProps={{ color: "textPrimary" }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label={`Filtrar ${label}`}
          type="text"
          fullWidth
          variant="outlined"
          disabled={loading}
          value={filter}
          onChange={handleFilterChange}
        />
      </Grid>

      <Grid item xs={12}>
        <div className={classes.list}>
          {loading ? (
            <>
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
              <LoadingItem />
            </>
          ) : (
            paginatedPerfis.map((perfil, index) => {
              const labelId = `perfil-list-item-${index}`;
              return (
                <ListItem
                  key={perfil.IdPerfil}
                  role="listitem"
                  onClick={() => handleTogglePerfil(perfil.IdPerfil)}
                  className={classes.listItem}
                  button
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedPerfilIds.includes(perfil.IdPerfil)}
                      tabIndex={-1}
                      inputProps={{ "aria-labelledby": labelId }}
                      color="primary"
                    />
                  </ListItemIcon>
                  <Tooltip title={perfil.Nome || ""}>
                    <ListItemText id={labelId} primary={perfil.Nome} />
                  </Tooltip>
                </ListItem>
              );
            })
          )}
        </div>
      </Grid>

      <Grid item xs={12}>
        <Pagination
          count={totalPages}
          page={currentPage + 1}
          onChange={handlePageClick}
          color="primary"
          size="small"
          variant="text"
          style={{ margin: "0 auto" }}
        />
      </Grid>
    </Grid>
  );
}
